import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import { Dialog } from "@mui/material";
import citiesData from "data/cities.json";
import prefecturesData from "data/prefectures.json";
import workOptionsData from "data/workOptions.json";
import { DialogType } from "app/types";
import { userIconPrefixes } from "app/assets";
import { AppDispatch, RootState } from "app/store";
import { useDialog, usePickParentImageAsset } from "app/hook";
import Constants from "common/constant";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import { FadeTransition, ParentCloseBtn, UserAvatar, Select } from "common/elements";
import I18n from "features/parent/i18n";
import { CheckMark } from "features/user/assets";
import { updateAccountProfile } from "features/parent/api";
import { GenerationStatus, useAIImageGeneration } from "features/generation";
import { CircularProgress } from "features/generation/components/CircularProgress";
import {
  ParentGender,
  AnnualIncome,
  Glasses,
  Hairstyle,
  BodyType,
  FaceShape,
  HouseholdMember,
} from "features/parent/types";

// ============================================================
// 型定義
// ============================================================

interface ParentIconEditModalProps {
  open: boolean;
  currentIcon: string;
  parentUid: string;
  onClose: () => void;
  onSave: (iconUrl: string) => void;
}

enum IconMethod {
  AI = "ai",
  UPLOAD = "upload",
  PRESET = "preset",
}

enum ProfileSubStep {
  GENDER = "gender",
  BIRTHDAY = "birthday",
  APPEARANCE = "appearance",
  WORK = "work",
  REGION = "region",
  FAMILY = "family",
  ICON_PREVIEW = "icon_preview",
}

interface IconEditState {
  icon: string;
  iconMethod: IconMethod | null;
}

interface ProfileState {
  gender: ParentGender | null;
  birthday: string;
  birthdayYear: number | null;
  birthdayMonth: number | null;
  birthdayDay: number | null;
  // 容姿
  glasses: Glasses | null;
  hairstyle: Hairstyle | null;
  bodyType: BodyType | null;
  faceShape: FaceShape | null;
  // 自分の役割
  userRole: string | null;
  // お勤め先
  industry: string | null;
  occupation: string | null;
  jobTitle: string | null;
  // 住所
  region: string;
  city: string;
  // 家族
  householdMembers: HouseholdMember[];
  boysCount: number;
  girlsCount: number;
  // 年収
  annual_income: AnnualIncome | null;
}

// ============================================================
// 定数
// ============================================================

const MODAL_WIDTH = 950;
const MODAL_HEIGHT = 900;

// カラー定数（ProfileSubStepBarと同じ）
const COLORS = {
  completed: "#70b1f4", // 青（btn-blueと同じ）
  current: "#3f4a61", // 現在ステップ
  pending: "#c2c9cf", // 未完了
  line: "#e0e4e7", // 接続線
};

// サイズ定数（ProfileSubStepBarと同じ）
const DOT_SIZE = 32;
const LINE_WIDTH = 2;
const LINE_HEIGHT = 32;

const PROFILE_SUB_STEPS = [
  { key: ProfileSubStep.GENDER, labelKey: "MSG_PROFILE_SUBSTEP_GENDER_BY_PARENT" },
  { key: ProfileSubStep.BIRTHDAY, labelKey: "MSG_PROFILE_SUBSTEP_BIRTHDAY_BY_PARENT" },
  { key: ProfileSubStep.APPEARANCE, labelKey: "MSG_PARENT_PROFILE_APPEARANCE" },
  { key: ProfileSubStep.WORK, labelKey: "MSG_PARENT_PROFILE_WORK" },
  { key: ProfileSubStep.REGION, labelKey: "MSG_PARENT_PROFILE_REGION" },
  { key: ProfileSubStep.FAMILY, labelKey: "MSG_PARENT_PROFILE_FAMILY" },
  { key: ProfileSubStep.ICON_PREVIEW, labelKey: "MSG_PROFILE_SUBSTEP_ICON_BY_PARENT" },
];

// 自分の役割オプション
const USER_ROLE_OPTIONS = [
  { key: "mother", labelKey: "MSG_HOUSEHOLD_MEMBER_MOTHER" },
  { key: "father", labelKey: "MSG_HOUSEHOLD_MEMBER_FATHER" },
  { key: "grandmother", labelKey: "MSG_HOUSEHOLD_MEMBER_GRANDMOTHER" },
  { key: "grandfather", labelKey: "MSG_HOUSEHOLD_MEMBER_GRANDFATHER" },
];

// 容姿オプション
const GLASSES_OPTIONS: { key: Glasses; labelKey: string }[] = [
  { key: Glasses.NONE, labelKey: "MSG_GLASSES_NONE_BY_PARENT" },
  { key: Glasses.GLASSES, labelKey: "MSG_GLASSES_YES_BY_PARENT" },
];

const HAIRSTYLE_OPTIONS: { key: Hairstyle; labelKey: string }[] = [
  { key: Hairstyle.VERY_SHORT, labelKey: "MSG_HAIRSTYLE_VERY_SHORT_BY_PARENT" },
  { key: Hairstyle.SHORT, labelKey: "MSG_HAIRSTYLE_SHORT_BY_PARENT" },
  { key: Hairstyle.MEDIUM, labelKey: "MSG_HAIRSTYLE_MEDIUM_BY_PARENT" },
  { key: Hairstyle.SLIGHTLY_LONG, labelKey: "MSG_HAIRSTYLE_SLIGHTLY_LONG_BY_PARENT" },
  { key: Hairstyle.LONG, labelKey: "MSG_HAIRSTYLE_LONG_BY_PARENT" },
];

const BODY_TYPE_OPTIONS: { key: BodyType; labelKey: string }[] = [
  { key: BodyType.THIN, labelKey: "MSG_BODY_TYPE_THIN_BY_PARENT" },
  { key: BodyType.SLIGHTLY_THIN, labelKey: "MSG_BODY_TYPE_SLIGHTLY_THIN_BY_PARENT" },
  { key: BodyType.MEDIUM, labelKey: "MSG_BODY_TYPE_MEDIUM_BY_PARENT" },
  { key: BodyType.SLIGHTLY_CHUBBY, labelKey: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY_BY_PARENT" },
  { key: BodyType.CHUBBY, labelKey: "MSG_BODY_TYPE_CHUBBY_BY_PARENT" },
];

const FACE_SHAPE_OPTIONS: { key: FaceShape; labelKey: string }[] = [
  { key: FaceShape.ROUND, labelKey: "MSG_FACE_SHAPE_ROUND_BY_PARENT" },
  { key: FaceShape.OVAL, labelKey: "MSG_FACE_SHAPE_OVAL_BY_PARENT" },
  { key: FaceShape.OBLONG, labelKey: "MSG_FACE_SHAPE_OBLONG_BY_PARENT" },
  { key: FaceShape.SQUARE, labelKey: "MSG_FACE_SHAPE_SQUARE_BY_PARENT" },
  { key: FaceShape.TRIANGLE, labelKey: "MSG_FACE_SHAPE_TRIANGLE_BY_PARENT" },
];

// 同居人オプション
const HOUSEHOLD_MEMBER_OPTIONS: { key: HouseholdMember; labelKey: string }[] = [
  { key: HouseholdMember.MOTHER, labelKey: "MSG_HOUSEHOLD_MEMBER_MOTHER" },
  { key: HouseholdMember.FATHER, labelKey: "MSG_HOUSEHOLD_MEMBER_FATHER" },
  { key: HouseholdMember.GRANDMOTHER, labelKey: "MSG_HOUSEHOLD_MEMBER_GRANDMOTHER" },
  { key: HouseholdMember.GRANDFATHER, labelKey: "MSG_HOUSEHOLD_MEMBER_GRANDFATHER" },
];

const ANNUAL_INCOME_OPTIONS: { key: AnnualIncome; labelKey: string }[] = [
  { key: AnnualIncome.UNDER_5M, labelKey: "MSG_PARENT_ONBOARDING_INCOME_UNDER_5M" },
  { key: AnnualIncome.M5_10M, labelKey: "MSG_PARENT_ONBOARDING_INCOME_5M_10M" },
  { key: AnnualIncome.OVER_10M, labelKey: "MSG_PARENT_ONBOARDING_INCOME_OVER_10M" },
  {
    key: AnnualIncome.PREFER_NOT_TO_SAY,
    labelKey: "MSG_PARENT_ONBOARDING_INCOME_PREFER_NOT_TO_SAY",
  },
];

const PARENT_GENDER_OPTIONS: {
  key: ParentGender;
  labelKey: string;
  bgColor: string;
  icon: string;
}[] = [
  {
    key: ParentGender.MALE,
    labelKey: "MSG_PARENT_ONBOARDING_GENDER_MALE",
    bgColor: "bg-blue-100",
    icon: "man",
  },
  {
    key: ParentGender.FEMALE,
    labelKey: "MSG_PARENT_ONBOARDING_GENDER_FEMALE",
    bgColor: "bg-red-100",
    icon: "woman",
  },
];

// ============================================================
// ヘルパー関数
// ============================================================

const isValidBirthday = (date: string): boolean => {
  if (!date) return false;
  if (date === "0000-00-00" || date.startsWith("0000")) return false;
  const d = dayjs(date);
  if (!d.isValid()) return false;
  if (d.year() < 1920) return false;
  return true;
};

const calculateAge = (birthday: string): number | undefined => {
  if (!birthday || !isValidBirthday(birthday)) return undefined;
  return dayjs().diff(dayjs(birthday), "year");
};

const isParentProfileComplete = (state: ProfileState): boolean => {
  if (!state.gender) return false;
  if (!isValidBirthday(state.birthday)) return false;
  if (!state.glasses) return false;
  if (!state.hairstyle) return false;
  if (!state.bodyType) return false;
  if (!state.faceShape) return false;
  if (!state.occupation) return false;
  if (!state.jobTitle) return false;
  if (!state.industry) return false;
  if (!state.annual_income) return false;
  if (!state.region) return false;
  if (!state.city) return false;
  if (!state.userRole) return false;
  if (state.householdMembers.length === 0) return false;
  return true;
};

const getParentCompletedStepIds = (state: ProfileState): string[] => {
  const ids: string[] = [];
  if (state.gender) ids.push("gender");
  if (isValidBirthday(state.birthday)) ids.push("birthday");
  if (state.glasses && state.hairstyle && state.bodyType && state.faceShape) ids.push("appearance");
  if (state.occupation && state.jobTitle && state.industry && state.annual_income) ids.push("work");
  if (state.region && state.city) ids.push("region");
  if (state.userRole && state.householdMembers.length > 0) ids.push("family");
  return ids;
};

// ============================================================
// コンポーネント
// ============================================================

export const ParentIconEditModal = ({
  open,
  currentIcon: _currentIcon,
  parentUid,
  onClose,
  onSave,
}: ParentIconEditModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const pickParentImageAsset = usePickParentImageAsset();
  const dispatch = useDispatch<AppDispatch>();
  const defaultAssets = useSelector((s: RootState) => s.config.assets);
  const parentProfile = useSelector((s: RootState) => s.parent.parentProfile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // プリセットアイコン（NONEカテゴリのみ）
  const presetIcons = useMemo(() => {
    try {
      type AssetItem = { fileSize: number; fileKey: string; category: string };
      return userIconPrefixes
        .reduce((pre: AssetItem[], cur: string) => [...pre, ...defaultAssets[cur]], [])
        .filter((a: AssetItem) => a.fileSize > 0 && a.category === "NONE")
        .map((a: AssetItem) => ({
          url: `${Constants.assetHost}/${a.fileKey}`,
        }));
    } catch {
      return [] as { url: string }[];
    }
  }, [defaultAssets]);

  // 生年月日Select用のref
  const yearRef = useRef<HTMLButtonElement>(null);
  const monthRef = useRef<HTMLButtonElement>(null);
  const dayRef = useRef<HTMLButtonElement>(null);

  // 容姿Select用のref
  const glassesRef = useRef<HTMLButtonElement>(null);
  const hairstyleRef = useRef<HTMLButtonElement>(null);
  const bodyTypeRef = useRef<HTMLButtonElement>(null);
  const faceShapeRef = useRef<HTMLButtonElement>(null);

  // お勤め先Select用のref
  const industryRef = useRef<HTMLButtonElement>(null);
  const occupationRef = useRef<HTMLButtonElement>(null);
  const jobTitleRef = useRef<HTMLButtonElement>(null);
  const incomeRef = useRef<HTMLButtonElement>(null);

  // お住まいSelect用のref
  const prefectureRef = useRef<HTMLButtonElement>(null);
  const cityRef = useRef<HTMLButtonElement>(null);

  // あなたの役割Select用のref
  const userRoleRef = useRef<HTMLButtonElement>(null);

  // 状態
  const [_uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [profileSubStep, setProfileSubStep] = useState<ProfileSubStep>(ProfileSubStep.GENDER);
  const [skipWizard, setSkipWizard] = useState(false);

  // 生年月日Select用のopen状態
  const [yearOpen, setYearOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);

  // 容姿Select用のopen状態
  const [glassesOpen, setGlassesOpen] = useState(false);
  const [hairstyleOpen, setHairstyleOpen] = useState(false);
  const [bodyTypeOpen, setBodyTypeOpen] = useState(false);
  const [faceShapeOpen, setFaceShapeOpen] = useState(false);

  // お勤め先Select用のopen状態
  const [industryOpen, setIndustryOpen] = useState(false);
  const [occupationOpen, setOccupationOpen] = useState(false);
  const [jobTitleOpen, setJobTitleOpen] = useState(false);
  const [incomeOpen, setIncomeOpen] = useState(false);

  // お住まいSelect用のopen状態
  const [prefectureOpen, setPrefectureOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);

  // あなたの役割Select用のopen状態
  const [userRoleOpen, setUserRoleOpen] = useState(false);

  const [state, setState] = useState<IconEditState>({
    icon: "",
    iconMethod: null,
  });

  // プロフィール状態（既存parentProfileから初期化）
  const [profileState, setProfileState] = useState<ProfileState>(() => {
    const existingBirthday = parentProfile?.birthday;
    const parsedDate =
      existingBirthday && isValidBirthday(existingBirthday) ? dayjs(existingBirthday) : null;
    return {
      gender: (parentProfile?.gender as ParentGender) || null,
      birthday: parsedDate ? parsedDate.format("YYYY-MM-DD") : "",
      birthdayYear: parsedDate ? parsedDate.year() : null,
      birthdayMonth: parsedDate ? parsedDate.month() + 1 : null,
      birthdayDay: parsedDate ? parsedDate.date() : null,
      glasses: (parentProfile?.glasses as Glasses) || null,
      hairstyle: (parentProfile?.hairstyle as Hairstyle) || null,
      bodyType: (parentProfile?.body_type as BodyType) || null,
      faceShape: (parentProfile?.face_shape as FaceShape) || null,
      userRole: parentProfile?.user_role || null,
      industry: parentProfile?.industry || null,
      occupation: parentProfile?.occupation || null,
      jobTitle: parentProfile?.job_title || null,
      region: parentProfile?.region || "",
      city: parentProfile?.city || "",
      householdMembers: (parentProfile?.household_members as HouseholdMember[]) || [],
      boysCount: parentProfile?.boys_count || 0,
      girlsCount: parentProfile?.girls_count || 0,
      annual_income: parentProfile?.annual_income || null,
    };
  });

  // 追加プロンプト（再生成時のユーザー入力）
  const [additionalPrompt, setAdditionalPrompt] = useState<string>("");

  const updateState = useCallback((updates: Partial<IconEditState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateProfileState = useCallback((updates: Partial<ProfileState>) => {
    setProfileState((prev) => ({ ...prev, ...updates }));
  }, []);

  // AI生成
  const { generate, status, progress, partialImage, results, reset, cancel } = useAIImageGeneration(
    {
      uid: parentUid,
      purpose: "parent_profile_icon",
    },
  );

  const isGenerating = status === GenerationStatus.GENERATING;
  const generatedImage = results.length > 0 ? results[0].url : null;

  // バリデーション（年・月・日がすべて選択されていることを確認）
  const isBirthdayValid =
    profileState.birthdayYear !== null &&
    profileState.birthdayMonth !== null &&
    profileState.birthdayDay !== null &&
    isValidBirthday(profileState.birthday);

  // サブステップ番号
  const subStepIndex = PROFILE_SUB_STEPS.findIndex((s) => s.key === profileSubStep);

  // ============================================================
  // ハンドラ
  // ============================================================

  const handleClose = () => {
    play();
    btnDelay(() => {
      setState({ icon: "", iconMethod: null });
      setUploadedFile(null);
      setProfileSubStep(ProfileSubStep.GENDER);
      setSkipWizard(false);
      setAdditionalPrompt("");
      reset();
      onClose();
    });
  };

  // AI生成をキャンセル
  const handleCancelGeneration = () => {
    play();
    cancel();
    reset();
    setAdditionalPrompt("");
    if (skipWizard) {
      updateState({ iconMethod: null });
      setSkipWizard(false);
    } else {
      setProfileSubStep(ProfileSubStep.FAMILY);
    }
  };

  // アイコン選択方法 - プロフィール完全性に応じてフロー分岐
  const handleSelectAI = () => {
    play();
    updateState({ iconMethod: IconMethod.AI });

    if (isParentProfileComplete(profileState)) {
      // プロフィール完備 → ウィザードスキップ、直接生成
      // ※プロフィールは既にサーバーに保存済みなので、ここでは保存しない
      setSkipWizard(true);
      setProfileSubStep(ProfileSubStep.ICON_PREVIEW);
      const age = calculateAge(profileState.birthday);
      generate({
        gender: profileState.gender === ParentGender.MALE ? "male" : "female",
        age,
        glasses: profileState.glasses || undefined,
        hairstyle: profileState.hairstyle || undefined,
        body_type: profileState.bodyType || undefined,
        face_shape: profileState.faceShape || undefined,
        user_role: profileState.userRole || undefined,
        occupation: profileState.occupation || undefined,
        job_title: profileState.jobTitle || undefined,
        industry: profileState.industry || undefined,
        annual_income: profileState.annual_income || undefined,
        region: profileState.region || undefined,
        city: profileState.city || undefined,
        household_members:
          profileState.householdMembers.length > 0 ? profileState.householdMembers : undefined,
        boys_count: profileState.boysCount || undefined,
        girls_count: profileState.girlsCount || undefined,
      });
    } else {
      // プロフィール不完全 → ウィザード表示
      setSkipWizard(false);
      setProfileSubStep(ProfileSubStep.GENDER);
    }
  };

  const handleSelectUpload = () => {
    play();
    updateState({ iconMethod: IconMethod.UPLOAD });
  };

  const handleSelectPreset = () => {
    play();
    updateState({ iconMethod: IconMethod.PRESET });
    if (presetIcons.length > 0) {
      updateState({ icon: presetIcons[0].url });
    }
  };

  // プリセットアイコン選択
  const handlePresetIconSelect = (url: string) => {
    play();
    updateState({ icon: url });
  };

  // プリセット選択確定
  const handlePresetConfirm = useCallback(async () => {
    if (!state.icon) return;
    play();
    handleDialog({ type: DialogType.LOADING, value: true });
    try {
      onSave(state.icon);
      toast(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
        </div>,
      );
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(I18n.t("MSG_TOAST_ERROR"));
    } finally {
      handleDialog({ type: DialogType.LOADING, value: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play, state.icon, handleDialog, onSave]);

  // アップロード処理（ファイル選択時に即座にアップロード）
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(I18n.t("MSG_TOAST_FILE_TOO_LARGE"));
      return;
    }

    // pickParentImageAssetを使用してアップロード（キャッシュ更新・limit更新が行われる）
    pickParentImageAsset(
      file,
      parentUid,
      (asset) => {
        updateState({ icon: asset.url });
        setUploadedFile(null);
      },
      () => {
        toast.error(I18n.t("MSG_TOAST_ERROR"));
      },
    );

    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSelectFile = () => {
    play();
    fileInputRef.current?.click();
  };

  // アップロード確定（すでにアップロード済みのURLを使用）
  const handleUploadConfirm = useCallback(() => {
    if (!state.icon) return;
    play();
    onSave(state.icon);
    toast(
      <div>
        <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
      </div>,
    );
    handleClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play, state.icon, onSave]);

  // Gender選択（選択のみ、遷移しない）
  const handleSelectGender = (gender: ParentGender) => {
    play();
    updateProfileState({ gender });
  };

  // Gender → Birthday
  const handleGenderNext = () => {
    play();
    btnDelay(() => {
      setProfileSubStep(ProfileSubStep.BIRTHDAY);
    });
  };

  const handleBirthdayNext = () => {
    play();
    btnDelay(() => {
      setProfileSubStep(ProfileSubStep.APPEARANCE);
    });
  };

  const handleAppearanceNext = () => {
    play();
    btnDelay(() => {
      setProfileSubStep(ProfileSubStep.WORK);
    });
  };

  const handleWorkNext = () => {
    play();
    btnDelay(() => {
      setProfileSubStep(ProfileSubStep.REGION);
    });
  };

  const handleRegionNext = () => {
    play();
    btnDelay(() => {
      setProfileSubStep(ProfileSubStep.FAMILY);
    });
  };

  // Family -> IconPreview (AI生成開始)
  const handleFamilyNext = () => {
    play();
    btnDelay(async () => {
      setProfileSubStep(ProfileSubStep.ICON_PREVIEW);

      // プロフィール情報を先に保存
      try {
        await dispatch(
          updateAccountProfile({
            uid: parentUid,
            profile: {
              ...(profileState.birthday && { birthday: profileState.birthday }),
              ...(profileState.gender && { gender: profileState.gender }),
              ...(profileState.glasses && { glasses: profileState.glasses }),
              ...(profileState.hairstyle && { hairstyle: profileState.hairstyle }),
              ...(profileState.bodyType && { body_type: profileState.bodyType }),
              ...(profileState.faceShape && { face_shape: profileState.faceShape }),
              ...(profileState.userRole && { user_role: profileState.userRole }),
              ...(profileState.industry && { industry: profileState.industry }),
              ...(profileState.occupation && { occupation: profileState.occupation }),
              ...(profileState.jobTitle && { job_title: profileState.jobTitle }),
              ...(profileState.annual_income && { annual_income: profileState.annual_income }),
              ...(profileState.region && { region: profileState.region }),
              ...(profileState.city && { city: profileState.city }),
              ...(profileState.householdMembers.length > 0 && {
                household_members: profileState.householdMembers,
              }),
              boys_count: profileState.boysCount,
              girls_count: profileState.girlsCount,
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error("Failed to save profile:", error);
      }

      // AI生成開始（全プロフィール情報を渡す）
      const age = calculateAge(profileState.birthday);
      generate({
        gender: profileState.gender === ParentGender.MALE ? "male" : "female",
        age,
        glasses: profileState.glasses || undefined,
        hairstyle: profileState.hairstyle || undefined,
        body_type: profileState.bodyType || undefined,
        face_shape: profileState.faceShape || undefined,
        user_role: profileState.userRole || undefined,
        occupation: profileState.occupation || undefined,
        job_title: profileState.jobTitle || undefined,
        industry: profileState.industry || undefined,
        annual_income: profileState.annual_income || undefined,
        region: profileState.region || undefined,
        city: profileState.city || undefined,
        household_members:
          profileState.householdMembers.length > 0 ? profileState.householdMembers : undefined,
        boys_count: profileState.boysCount || undefined,
        girls_count: profileState.girlsCount || undefined,
      });
    });
  };

  // AI再生成
  const _handleRegenerate = () => {
    play();
    reset();
    const age = calculateAge(profileState.birthday);
    generate({
      gender: profileState.gender === ParentGender.MALE ? "male" : "female",
      age,
      glasses: profileState.glasses || undefined,
      hairstyle: profileState.hairstyle || undefined,
      body_type: profileState.bodyType || undefined,
      face_shape: profileState.faceShape || undefined,
      user_role: profileState.userRole || undefined,
      occupation: profileState.occupation || undefined,
      job_title: profileState.jobTitle || undefined,
      industry: profileState.industry || undefined,
      annual_income: profileState.annual_income || undefined,
      region: profileState.region || undefined,
      city: profileState.city || undefined,
      household_members:
        profileState.householdMembers.length > 0 ? profileState.householdMembers : undefined,
      boys_count: profileState.boysCount || undefined,
      girls_count: profileState.girlsCount || undefined,
    });
  };

  // AI追加プロンプト付き再生成
  const handleRegenerateWithPrompt = () => {
    play();
    reset();
    const age = calculateAge(profileState.birthday);
    generate({
      gender: profileState.gender === ParentGender.MALE ? "male" : "female",
      age,
      glasses: profileState.glasses || undefined,
      hairstyle: profileState.hairstyle || undefined,
      body_type: profileState.bodyType || undefined,
      face_shape: profileState.faceShape || undefined,
      user_role: profileState.userRole || undefined,
      occupation: profileState.occupation || undefined,
      job_title: profileState.jobTitle || undefined,
      industry: profileState.industry || undefined,
      annual_income: profileState.annual_income || undefined,
      region: profileState.region || undefined,
      city: profileState.city || undefined,
      household_members:
        profileState.householdMembers.length > 0 ? profileState.householdMembers : undefined,
      boys_count: profileState.boysCount || undefined,
      girls_count: profileState.girlsCount || undefined,
      additional_prompt: additionalPrompt || undefined,
    });
  };

  // 追加プロンプト変更ハンドラー
  const handleAdditionalPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 20);
    setAdditionalPrompt(value);
  };

  // AI生成結果確定（アイコンのみ保存、プロフィールは生成開始時に保存済み）
  const handleIconConfirm = useCallback(() => {
    if (!generatedImage) return;
    play();
    // アイコンを保存
    onSave(generatedImage);
    toast(
      <div>
        <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
      </div>,
    );
    handleClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play, generatedImage, onSave]);

  // サブステップバーをタップして遷移
  const handleSubStepClick = (targetIndex: number) => {
    const targetStep = PROFILE_SUB_STEPS[targetIndex];
    // 現在のステップはクリック不可
    if (targetIndex === subStepIndex) return;
    play();
    btnDelay(() => {
      setProfileSubStep(targetStep.key);
    });
  };

  // 戻るボタン
  const handleBack = useCallback(() => {
    play();
    btnDelay(() => {
      if (state.iconMethod === null) {
        handleClose();
      } else if (state.iconMethod === IconMethod.AI) {
        // AI生成フロー内の戻る
        if (profileSubStep === ProfileSubStep.GENDER) {
          updateState({ iconMethod: null });
        } else if (profileSubStep === ProfileSubStep.BIRTHDAY) {
          setProfileSubStep(ProfileSubStep.GENDER);
        } else if (profileSubStep === ProfileSubStep.APPEARANCE) {
          setProfileSubStep(ProfileSubStep.BIRTHDAY);
        } else if (profileSubStep === ProfileSubStep.WORK) {
          setProfileSubStep(ProfileSubStep.APPEARANCE);
        } else if (profileSubStep === ProfileSubStep.REGION) {
          setProfileSubStep(ProfileSubStep.WORK);
        } else if (profileSubStep === ProfileSubStep.FAMILY) {
          setProfileSubStep(ProfileSubStep.REGION);
        } else if (profileSubStep === ProfileSubStep.ICON_PREVIEW) {
          if (skipWizard) {
            // スキップモードではメソッド選択画面に戻る
            updateState({ iconMethod: null });
            setSkipWizard(false);
          } else {
            setProfileSubStep(ProfileSubStep.FAMILY);
          }
          setAdditionalPrompt("");
          reset();
        }
      } else if (state.iconMethod === IconMethod.UPLOAD) {
        updateState({ icon: "", iconMethod: null });
        setUploadedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else if (state.iconMethod === IconMethod.PRESET) {
        updateState({ icon: "", iconMethod: null });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play, state.iconMethod, profileSubStep, updateState, reset, skipWizard]);

  // ============================================================
  // Selectオプション
  // ============================================================

  const glassesOptions = useMemo(
    () => GLASSES_OPTIONS.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const hairstyleOptions = useMemo(
    () => HAIRSTYLE_OPTIONS.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const bodyTypeOptions = useMemo(
    () => BODY_TYPE_OPTIONS.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const faceShapeOptions = useMemo(
    () => FACE_SHAPE_OPTIONS.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const industryOptions = useMemo(
    () => workOptionsData.industry.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const occupationOptions = useMemo(
    () =>
      workOptionsData.occupation.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const jobTitleOptions = useMemo(
    () => workOptionsData.jobTitle.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const incomeOptions = useMemo(
    () => ANNUAL_INCOME_OPTIONS.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const userRoleSelectOptions = useMemo(
    () => USER_ROLE_OPTIONS.map((opt) => ({ value: opt.key, label: I18n.t(opt.labelKey) })),
    [],
  );
  const prefectureOptions = useMemo(
    () => prefecturesData.prefectures.map((pref) => ({ value: pref.code, label: pref.name })),
    [],
  );
  const cityOptions = useMemo(() => {
    if (!profileState.region) return [];
    const cities =
      (citiesData as Record<string, { code: string; name: string }[]>)[profileState.region] || [];
    return cities.map((city) => ({ value: city.code, label: city.name }));
  }, [profileState.region]);

  // ============================================================
  // レンダリング
  // ============================================================

  // サブステップバー
  const renderSubStepBar = () => {
    const completedStepIds = getParentCompletedStepIds(profileState);
    return (
      <div className="flex-col-el py-6 pr-8">
        {PROFILE_SUB_STEPS.map((step, index) => {
          const isCurrent = index === subStepIndex;
          const isCompleted = isCurrent ? false : completedStepIds.includes(step.key);
          const isLast = index === PROFILE_SUB_STEPS.length - 1;
          const isClickable = index !== subStepIndex;

          return (
            <div key={step.key} className="flex-row-el">
              {/* 左側：アイコンと接続線 */}
              <div className="flex-col-el items-center" style={{ width: DOT_SIZE }}>
                {/* ドット/アイコン */}
                <div
                  className={`flex-col-el flex-center rounded-full ${isClickable ? "cursor-pointer" : ""}`}
                  style={{
                    width: DOT_SIZE,
                    height: DOT_SIZE,
                    backgroundColor: isCompleted
                      ? COLORS.completed
                      : isCurrent
                        ? COLORS.current
                        : "transparent",
                    border:
                      !isCompleted && !isCurrent
                        ? `${LINE_WIDTH}px solid ${COLORS.pending}`
                        : "none",
                  }}
                  onClick={() => handleSubStepClick(index)}
                >
                  {isCompleted && (
                    <span
                      className="material-symbols-outlined text-[20px]"
                      style={{ color: "#FFFFFF" }}
                    >
                      check
                    </span>
                  )}
                  {isCurrent && (
                    <div
                      className="rounded-full"
                      style={{
                        width: 10,
                        height: 10,
                        backgroundColor: "#FFFFFF",
                      }}
                    />
                  )}
                </div>
                {/* 接続線 */}
                {!isLast && (
                  <div
                    style={{
                      width: LINE_WIDTH,
                      height: LINE_HEIGHT,
                      backgroundColor: isCompleted ? COLORS.completed : COLORS.line,
                    }}
                  />
                )}
              </div>

              {/* 右側：ラベル */}
              <p
                className={`text-body text-[24px] whitespace-nowrap !ml-3 ${isClickable ? "cursor-pointer" : ""}`}
                style={{
                  color: isCompleted
                    ? COLORS.completed
                    : isCurrent
                      ? COLORS.current
                      : COLORS.pending,
                  lineHeight: `${DOT_SIZE}px`,
                }}
                onClick={() => handleSubStepClick(index)}
              >
                {I18n.t(step.labelKey)}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  // アイコン選択方法
  const renderIconMethod = () => (
    <div className="flex-col-el flex-center w-full h-full">
      <div className="flex-col-el flex-center mb-8">
        <p className="text-body text-textcolor-black text-[32px]">
          {I18n.t("MSG_PARENT_ONBOARDING_ICON_METHOD_TITLE")}
        </p>
      </div>
      <div className="flex-row-el flex-center gap-4">
        {/* プリセットから選択 */}
        <div
          onClick={handleSelectPreset}
          className="flex-col-el items-center justify-center w-[220px] h-[220px] rounded-[4px] bg-green-100 cursor-pointer select-none"
        >
          <span className="material-symbols-outlined !text-5xl text-white mb-3">grid_view</span>
          <p className="text-body text-[22px] text-white text-center whitespace-pre-line">
            {I18n.t("MSG_PARENT_ONBOARDING_ICON_PRESET")}
          </p>
        </div>

        {/* アップロード */}
        <div
          onClick={handleSelectUpload}
          className="flex-col-el items-center justify-center w-[220px] h-[220px] rounded-[4px] bg-orange-100 cursor-pointer select-none"
        >
          <span className="material-symbols-outlined !text-5xl text-white mb-3">upload</span>
          <p className="text-body text-[22px] text-white text-center whitespace-pre-line">
            {I18n.t("MSG_PARENT_ONBOARDING_ICON_UPLOAD")}
          </p>
        </div>

        {/* プロフィールから作る */}
        <div
          onClick={handleSelectAI}
          className="flex-col-el flex-center w-[220px] h-[220px] rounded-[4px] bg-blue-100 cursor-pointer select-none"
        >
          <span className="material-symbols-outlined !text-5xl text-white mb-3 mt-8">
            auto_awesome
          </span>
          <p className="text-body text-[22px] text-white text-center whitespace-pre-line">
            {I18n.t("MSG_PARENT_ONBOARDING_ICON_PROFILE_AI")}
          </p>
        </div>
      </div>
    </div>
  );

  // プリセットアイコン選択
  const renderPresetIconStep = () => (
    <div className="flex-col-el items-center w-full h-full pt-8">
      <div className="flex-col-el flex-center mb-6">
        <p className="text-body text-textcolor-black text-[32px]">
          {I18n.t("MSG_PARENT_ONBOARDING_ICON_PRESET_TITLE")}
        </p>
      </div>
      <div className="grid grid-cols-5 grid-rows-3 w-fit gap-6">
        {presetIcons.slice(0, 15).map((icon) => (
          <div
            key={icon.url}
            onClick={() => handlePresetIconSelect(icon.url)}
            className="flex-col-center w-[84px] h-[84px] cursor-pointer"
          >
            {state.icon === icon.url && (
              <div className="flex-col-el absolute top-[-16px] left-[-16px]">
                <CheckMark />
              </div>
            )}
            <img
              alt="preset-icon"
              src={icon.url}
              className={`w-full h-full object-contain border-solid ${
                state.icon === icon.url
                  ? "border-8 border-green-100/40"
                  : "border-4 border-gray-80/40"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );

  // 性別選択
  const renderGenderStep = () => (
    <div className="flex-col-el items-center w-full h-full">
      <p className="text-body text-textcolor-black text-[24px] text-center pt-6">
        {I18n.t("MSG_PROFILE_STEP_GENDER_DESC")}
      </p>
      <div className="flex-1 flex-col-el items-center justify-center w-full">
        <div className="flex-row-el flex-center gap-6">
          {PARENT_GENDER_OPTIONS.map((option) => (
            <div
              key={option.key}
              onClick={() => handleSelectGender(option.key)}
              className={`
                flex-col-el items-center justify-center w-[200px] h-[200px] rounded-[8px]
                cursor-pointer select-none              ${profileState.gender === option.key ? option.bgColor : "bg-gray2-40"}
              `}
            >
              <span className="material-symbols-outlined !text-6xl text-white mb-3">
                {option.icon}
              </span>
              <span className="text-[24px] text-white text-center px-2">
                {I18n.t(option.labelKey)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // 生年月日選択
  const renderBirthdayStep = () => {
    const selectedYear = profileState.birthdayYear;
    const selectedMonth = profileState.birthdayMonth;
    const selectedDay = profileState.birthdayDay;

    const currentYear = dayjs().year();
    const yearOptions = Array.from({ length: currentYear - 1920 + 1 }, (_, i) => {
      const year = currentYear - i;
      return { value: year, label: `${year}${I18n.t("MSG_PARENT_PAGE_YEAR")}` };
    });

    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}${I18n.t("MSG_PARENT_PAGE_MONTH")}`,
    }));

    const daysInMonth =
      selectedYear && selectedMonth
        ? dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth()
        : 31;
    const dayOptions = Array.from({ length: daysInMonth }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}${I18n.t("MSG_PARENT_PAGE_DAY")}`,
    }));

    const handleYearChange = (year: number) => {
      const month = selectedMonth;
      const day = selectedDay;
      // 日の調整（月が選択済みの場合のみ）
      let validDay = day;
      if (month && day) {
        const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
        validDay = Math.min(day, maxDay);
      }
      // birthdayYearを更新し、3つ全てが揃った場合のみbirthdayを設定
      if (month && validDay) {
        updateProfileState({
          birthdayYear: year,
          birthdayDay: validDay,
          birthday: dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"),
        });
      } else {
        updateProfileState({
          birthdayYear: year,
          birthday: "",
        });
      }
    };

    const handleMonthChange = (month: number) => {
      const year = selectedYear;
      const day = selectedDay;
      // 日の調整（年と日が選択済みの場合のみ）
      let validDay = day;
      if (year && day) {
        const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
        validDay = Math.min(day, maxDay);
      }
      // birthdayMonthを更新し、3つ全てが揃った場合のみbirthdayを設定
      if (year && validDay) {
        updateProfileState({
          birthdayMonth: month,
          birthdayDay: validDay,
          birthday: dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"),
        });
      } else {
        updateProfileState({
          birthdayMonth: month,
          birthday: "",
        });
      }
    };

    const handleDayChange = (day: number) => {
      const year = selectedYear;
      const month = selectedMonth;
      // birthdayDayを更新し、3つ全てが揃った場合のみbirthdayを設定
      if (year && month) {
        updateProfileState({
          birthdayDay: day,
          birthday: dayjs(`${year}-${month}-${day}`).format("YYYY-MM-DD"),
        });
      } else {
        updateProfileState({
          birthdayDay: day,
          birthday: "",
        });
      }
    };

    const buttonBase =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black";
    const yearButtonClass = `${buttonBase} w-[160px]`;
    const monthDayButtonClass = `${buttonBase} w-[130px]`;

    return (
      <div className="flex-col-el items-center w-full h-full">
        <p className="text-body text-textcolor-black text-[24px] text-center pt-6">
          {I18n.t("MSG_PROFILE_STEP_BIRTHDAY_DESC")}
        </p>
        <div className="flex-1 flex-col-el items-center justify-center w-full">
          <div className="flex-row-el flex-center gap-4">
            <button
              ref={yearRef}
              onClick={() => {
                play();
                setYearOpen(true);
              }}
              className={yearButtonClass}
            >
              <span>
                {selectedYear
                  ? `${selectedYear}${I18n.t("MSG_PARENT_PAGE_YEAR")}`
                  : I18n.t("MSG_PARENT_PAGE_YEAR")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={yearRef}
              open={yearOpen}
              onClose={() => setYearOpen(false)}
              value={selectedYear}
              onChange={handleYearChange}
              options={yearOptions}
            />

            <button
              ref={monthRef}
              onClick={() => {
                play();
                setMonthOpen(true);
              }}
              className={monthDayButtonClass}
            >
              <span>
                {selectedMonth
                  ? `${selectedMonth}${I18n.t("MSG_PARENT_PAGE_MONTH")}`
                  : I18n.t("MSG_PARENT_PAGE_MONTH")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={monthRef}
              open={monthOpen}
              onClose={() => setMonthOpen(false)}
              value={selectedMonth}
              onChange={handleMonthChange}
              options={monthOptions}
            />

            <button
              ref={dayRef}
              onClick={() => {
                play();
                setDayOpen(true);
              }}
              className={monthDayButtonClass}
            >
              <span>
                {selectedDay
                  ? `${selectedDay}${I18n.t("MSG_PARENT_PAGE_DAY")}`
                  : I18n.t("MSG_PARENT_PAGE_DAY")}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={dayRef}
              open={dayOpen}
              onClose={() => setDayOpen(false)}
              value={selectedDay}
              onChange={handleDayChange}
              options={dayOptions}
            />
          </div>
        </div>
      </div>
    );
  };

  // アップロード画面
  const renderUploadIconStep = () => (
    <div className="flex-col-el flex-center w-full h-full">
      <div className="flex-col-el flex-center mb-8">
        <p className="text-body text-textcolor-black text-[32px]">
          {I18n.t("MSG_PARENT_ONBOARDING_UPLOAD_TITLE")}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="flex-col-el flex-center">
        {state.icon ? (
          <div className="relative cursor-pointer group" onClick={handleSelectFile}>
            <UserAvatar size={200} icon={state.icon} />
            <div className="absolute bottom-0 right-0 w-16 h-16 rounded-full bg-gray2-100 flex items-center justify-center shadow-md">
              <span className="material-symbols-outlined text-[54px] text-white">photo_camera</span>
            </div>
          </div>
        ) : (
          <div
            onClick={handleSelectFile}
            className="flex-col-el flex-center w-[400px] h-[250px] border-2 border-dashed rounded-[12px] cursor-pointer transition-all border-gray2-60 hover:border-blue-100"
          >
            <span className="material-symbols-outlined !text-[64px] text-gray2-60 mb-4">
              cloud_upload
            </span>
            <p className="text-[18px] text-textcolor-black mb-2">
              {I18n.t("MSG_UPLOAD_ICON_DRAG_DROP_BY_PARENT")}
            </p>
            <p className="text-[14px] text-gray2-80">{I18n.t("MSG_UPLOAD_ICON_CLICK_BY_PARENT")}</p>
          </div>
        )}
      </div>

      {/* ファイルサイズ・形式の注意書き */}
      <p className="text-[14px] text-gray2-80 mt-4">{I18n.t("MSG_UPLOAD_ICON_HINT_BY_PARENT")}</p>
    </div>
  );

  // 容姿選択
  const renderAppearanceStep = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    return (
      <div className="flex-col-el items-center w-full h-full">
        <p className="text-body text-textcolor-black text-[24px] text-center pt-6">
          {I18n.t("MSG_PROFILE_STEP_APPEARANCE_DESC")}
        </p>
        <div className="flex-1 flex-col-el items-center justify-center w-full">
          <div className="flex-col-el gap-5 w-full max-w-[500px]">
            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[100px] shrink-0">
                {I18n.t("MSG_GLASSES_TITLE_BY_PARENT")}
              </p>
              <div className="flex-1">
                <button
                  ref={glassesRef}
                  onClick={() => {
                    play();
                    setGlassesOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.glasses
                      ? glassesOptions.find((o) => o.value === profileState.glasses)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={glassesRef}
                  open={glassesOpen}
                  onClose={() => setGlassesOpen(false)}
                  value={profileState.glasses || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ glasses: (value as Glasses) || null });
                  }}
                  options={glassesOptions}
                />
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[100px] shrink-0">
                {I18n.t("MSG_HAIRSTYLE_TITLE_BY_PARENT")}
              </p>
              <div className="flex-1">
                <button
                  ref={hairstyleRef}
                  onClick={() => {
                    play();
                    setHairstyleOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.hairstyle
                      ? hairstyleOptions.find((o) => o.value === profileState.hairstyle)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={hairstyleRef}
                  open={hairstyleOpen}
                  onClose={() => setHairstyleOpen(false)}
                  value={profileState.hairstyle || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ hairstyle: (value as Hairstyle) || null });
                  }}
                  options={hairstyleOptions}
                />
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[100px] shrink-0">
                {I18n.t("MSG_BODY_TYPE_TITLE_BY_PARENT")}
              </p>
              <div className="flex-1">
                <button
                  ref={bodyTypeRef}
                  onClick={() => {
                    play();
                    setBodyTypeOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.bodyType
                      ? bodyTypeOptions.find((o) => o.value === profileState.bodyType)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={bodyTypeRef}
                  open={bodyTypeOpen}
                  onClose={() => setBodyTypeOpen(false)}
                  value={profileState.bodyType || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ bodyType: (value as BodyType) || null });
                  }}
                  options={bodyTypeOptions}
                />
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[100px] shrink-0">
                {I18n.t("MSG_FACE_SHAPE_TITLE_BY_PARENT")}
              </p>
              <div className="flex-1">
                <button
                  ref={faceShapeRef}
                  onClick={() => {
                    play();
                    setFaceShapeOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.faceShape
                      ? faceShapeOptions.find((o) => o.value === profileState.faceShape)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={faceShapeRef}
                  open={faceShapeOpen}
                  onClose={() => setFaceShapeOpen(false)}
                  value={profileState.faceShape || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ faceShape: (value as FaceShape) || null });
                  }}
                  options={faceShapeOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // お勤め先選択
  const renderWorkStep = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    return (
      <div className="flex-col-el items-center w-full h-full">
        <p className="text-body text-textcolor-black text-[24px] text-center pt-6">
          {I18n.t("MSG_PROFILE_STEP_WORK_DESC")}
        </p>
        <div className="flex-1 flex-col-el items-center justify-center w-full">
          <div className="flex-col-el gap-5 w-full max-w-[500px]">
            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[120px] shrink-0">
                {I18n.t("MSG_OCCUPATION_TITLE")}
              </p>
              <div className="flex-1">
                <button
                  ref={occupationRef}
                  onClick={() => {
                    play();
                    setOccupationOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.occupation
                      ? occupationOptions.find((o) => o.value === profileState.occupation)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={occupationRef}
                  open={occupationOpen}
                  onClose={() => setOccupationOpen(false)}
                  value={profileState.occupation || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ occupation: value || null });
                  }}
                  options={occupationOptions}
                />
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[120px] shrink-0">
                {I18n.t("MSG_JOB_TITLE_LABEL")}
              </p>
              <div className="flex-1">
                <button
                  ref={jobTitleRef}
                  onClick={() => {
                    play();
                    setJobTitleOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.jobTitle
                      ? jobTitleOptions.find((o) => o.value === profileState.jobTitle)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={jobTitleRef}
                  open={jobTitleOpen}
                  onClose={() => setJobTitleOpen(false)}
                  value={profileState.jobTitle || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ jobTitle: value || null });
                  }}
                  options={jobTitleOptions}
                />
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[120px] shrink-0">
                {I18n.t("MSG_INDUSTRY_TITLE")}
              </p>
              <div className="flex-1">
                <button
                  ref={industryRef}
                  onClick={() => {
                    play();
                    setIndustryOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.industry
                      ? industryOptions.find((o) => o.value === profileState.industry)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={industryRef}
                  open={industryOpen}
                  onClose={() => setIndustryOpen(false)}
                  value={profileState.industry || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ industry: value || null });
                  }}
                  options={industryOptions}
                />
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[120px] shrink-0">
                {I18n.t("MSG_PARENT_ONBOARDING_INCOME_TITLE")}
              </p>
              <div className="flex-1">
                <button
                  ref={incomeRef}
                  onClick={() => {
                    play();
                    setIncomeOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.annual_income
                      ? incomeOptions.find((o) => o.value === profileState.annual_income)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={incomeRef}
                  open={incomeOpen}
                  onClose={() => setIncomeOpen(false)}
                  value={profileState.annual_income || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ annual_income: (value as AnnualIncome) || null });
                  }}
                  options={incomeOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // お住まいの地域
  const renderRegionStep = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    return (
      <div className="flex-col-el items-center w-full h-full">
        <p className="text-body text-textcolor-black text-[24px] text-center pt-6">
          {I18n.t("MSG_PROFILE_STEP_REGION_DESC")}
        </p>
        <div className="flex-1 flex-col-el items-center justify-center w-full">
          <div className="flex-col-el gap-5 w-full max-w-[500px]">
            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[100px] shrink-0">
                {I18n.t("MSG_PREFECTURE_TITLE")}
              </p>
              <div className="flex-1">
                <button
                  ref={prefectureRef}
                  onClick={() => {
                    play();
                    setPrefectureOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.region
                      ? prefectureOptions.find((o) => o.value === profileState.region)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={prefectureRef}
                  open={prefectureOpen}
                  onClose={() => setPrefectureOpen(false)}
                  value={profileState.region || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ region: value || "", city: "" });
                  }}
                  options={prefectureOptions}
                />
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[100px] shrink-0">
                {I18n.t("MSG_CITY_TITLE")}
              </p>
              <div className="flex-1">
                <button
                  ref={cityRef}
                  onClick={() => {
                    play();
                    setCityOpen(true);
                  }}
                  className={buttonClass}
                  disabled={!profileState.region}
                >
                  <span>
                    {profileState.city
                      ? cityOptions.find((o) => o.value === profileState.city)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={cityRef}
                  open={cityOpen}
                  onClose={() => setCityOpen(false)}
                  value={profileState.city || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ city: value || "" });
                  }}
                  options={cityOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 家族構成
  const renderFamilyStep = () => {
    const buttonClass =
      "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[24px] cursor-pointer select-none bg-white border-4 border-input-focus text-textcolor-black w-full";

    return (
      <div className="flex-col-el items-center w-full h-full">
        <p className="text-body text-textcolor-black text-[24px] text-center pt-6">
          {I18n.t("MSG_PROFILE_STEP_FAMILY_DESC")}
        </p>
        <div className="flex-1 flex-col-el items-center justify-center w-full">
          <div className="flex-col-el gap-5 w-full max-w-[600px]">
            {/* あなたの役割 */}
            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[130px] shrink-0">
                {I18n.t("MSG_USER_ROLE_TITLE")}
              </p>
              <div className="flex-1">
                <button
                  ref={userRoleRef}
                  onClick={() => {
                    play();
                    setUserRoleOpen(true);
                  }}
                  className={buttonClass}
                >
                  <span>
                    {profileState.userRole
                      ? userRoleSelectOptions.find((o) => o.value === profileState.userRole)?.label
                      : I18n.t("MSG_PARENT_ONBOARDING_BIRTHDAY_SELECT")}
                  </span>
                  <span className="material-symbols-outlined !text-[24px]">expand_more</span>
                </button>
                <Select
                  anchorRef={userRoleRef}
                  open={userRoleOpen}
                  onClose={() => setUserRoleOpen(false)}
                  value={profileState.userRole || ""}
                  onChange={(value) => {
                    play();
                    updateProfileState({ userRole: value || null });
                  }}
                  options={userRoleSelectOptions}
                />
              </div>
            </div>

            {/* 同居人 */}
            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[130px] shrink-0">
                {I18n.t("MSG_HOUSEHOLD_MEMBERS_TITLE")}
              </p>
              <div className="flex-row-el flex-wrap gap-3">
                {HOUSEHOLD_MEMBER_OPTIONS.map((opt) => {
                  const isActive = profileState.householdMembers.includes(opt.key);
                  return (
                    <div
                      key={opt.key}
                      onClick={() => {
                        play();
                        if (isActive) {
                          updateProfileState({
                            householdMembers: profileState.householdMembers.filter(
                              (m) => m !== opt.key,
                            ),
                          });
                        } else {
                          updateProfileState({
                            householdMembers: [...profileState.householdMembers, opt.key],
                          });
                        }
                      }}
                      className={`py-3 px-6 rounded-[10px] border-2 text-[21px] cursor-pointer select-none transition-all ${
                        isActive
                          ? "bg-green-alt border-green-alt text-textcolor-black"
                          : "bg-surface border-green-alt text-textcolor-black/70"
                      }`}
                    >
                      {I18n.t(opt.labelKey)}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[130px] shrink-0">
                {I18n.t("MSG_BOYS_COUNT")} {profileState.boysCount}人
              </p>
              <div className="flex-1 flex-row-el items-center gap-2">
                {[1, 2, 3, 4, 5].map((num) => (
                  <span
                    key={num}
                    onClick={() => {
                      play();
                      updateProfileState({
                        boysCount: profileState.boysCount === num ? 0 : num,
                      });
                    }}
                    className={`material-symbols-outlined !text-[72px] cursor-pointer select-none ${
                      num <= profileState.boysCount ? "text-blue-100" : "text-gray-40"
                    }`}
                  >
                    boy
                  </span>
                ))}
              </div>
            </div>

            <div className="flex-row-el items-center gap-4">
              <p className="text-body text-textcolor-black text-[24px] w-[130px] shrink-0">
                {I18n.t("MSG_GIRLS_COUNT")} {profileState.girlsCount}人
              </p>
              <div className="flex-1 flex-row-el items-center gap-2">
                {[1, 2, 3, 4, 5].map((num) => (
                  <span
                    key={num}
                    onClick={() => {
                      play();
                      updateProfileState({
                        girlsCount: profileState.girlsCount === num ? 0 : num,
                      });
                    }}
                    className={`material-symbols-outlined !text-[72px] cursor-pointer select-none ${
                      num <= profileState.girlsCount ? "text-red-100" : "text-gray-40"
                    }`}
                  >
                    girl
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // アイコンプレビュー
  const renderIconPreviewStep = () => {
    const displayImage = generatedImage || partialImage;
    const isComplete = status === GenerationStatus.COMPLETE;

    return (
      <div className="flex-col-el flex-center w-full h-full">
        {isGenerating ? (
          <div className="flex-col-el flex-center w-[200px] h-[200px] bg-beige-20 rounded-full border-4 border-gray-20 overflow-hidden">
            <CircularProgress progress={progress} size={184} />
          </div>
        ) : displayImage ? (
          <UserAvatar size={200} icon={displayImage} />
        ) : (
          <div className="flex-col-el flex-center w-[200px] h-[200px] bg-beige-20 rounded-full border-4 border-gray-20 overflow-hidden">
            <span className="material-symbols-outlined text-gray-40 !text-[80px]">
              auto_awesome
            </span>
          </div>
        )}
        <p className="text-body text-textcolor-black text-[24px] mt-6">
          {isGenerating
            ? I18n.t("MSG_PARENT_ONBOARDING_ICON_PREVIEW_GENERATING")
            : I18n.t("MSG_PARENT_ONBOARDING_ICON_PREVIEW_CONFIRM")}
        </p>

        {/* 追加プロンプト入力フォーム（生成完了後のみ表示） */}
        {isComplete && (
          <div className="flex-row-el items-center justify-center gap-4 mt-6">
            <div className="relative">
              <input
                type="text"
                value={additionalPrompt}
                onChange={handleAdditionalPromptChange}
                placeholder={I18n.t("MSG_ICON_ADDITIONAL_PROMPT_PLACEHOLDER")}
                maxLength={20}
                className="w-[400px] box-border px-4 py-3 pr-[70px] rounded-[8px] border-4 border-input-focus text-[24px] text-textcolor-black bg-white focus:outline-none transition-all"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[18px] text-gray2-80">
                {additionalPrompt.length}/20
              </span>
            </div>
            <button
              onClick={handleRegenerateWithPrompt}
              className="flex-row-el items-center justify-center gap-2 w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
            >
              <span>{I18n.t("MSG_ICON_CREATE_BTN")}</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // AI生成時のサブステップコンテンツ
  const renderAISubStepContent = () => {
    switch (profileSubStep) {
      case ProfileSubStep.GENDER:
        return renderGenderStep();
      case ProfileSubStep.BIRTHDAY:
        return renderBirthdayStep();
      case ProfileSubStep.APPEARANCE:
        return renderAppearanceStep();
      case ProfileSubStep.WORK:
        return renderWorkStep();
      case ProfileSubStep.REGION:
        return renderRegionStep();
      case ProfileSubStep.FAMILY:
        return renderFamilyStep();
      case ProfileSubStep.ICON_PREVIEW:
        return renderIconPreviewStep();
      default:
        return null;
    }
  };

  // コンテンツ
  const renderContent = () => {
    if (state.iconMethod === null) {
      return renderIconMethod();
    }
    if (state.iconMethod === IconMethod.AI) {
      return (
        <div className="flex-row-el w-full h-full">
          {!skipWizard && renderSubStepBar()}
          <div className="flex-col-el flex-center flex-1 min-h-0">{renderAISubStepContent()}</div>
        </div>
      );
    }
    if (state.iconMethod === IconMethod.UPLOAD) {
      return renderUploadIconStep();
    }
    if (state.iconMethod === IconMethod.PRESET) {
      return renderPresetIconStep();
    }
    return null;
  };

  // ボタンスタイル定数
  const btnStylePrimary =
    "flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all";
  const btnStyleCancel =
    "flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all";
  const btnStyleDisabled =
    "flex-row-el items-center justify-center w-[160px] py-3 bg-gray2-40 text-white border-none rounded-[12px] text-[24px] cursor-not-allowed opacity-50";
  const btnStyleDanger =
    "flex-row-el items-center justify-center w-[200px] py-3 bg-red-100 text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all";
  const _btnStyleBlue =
    "flex-row-el items-center justify-center w-[160px] py-3 bg-blue-100 text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all";

  // フッター
  const renderFooter = () => {
    let primaryButton = null;

    if (state.iconMethod === IconMethod.AI) {
      // AI生成フロー内のサブステップに応じたボタン
      if (profileSubStep === ProfileSubStep.GENDER) {
        const isGenderDisabled =
          profileState.gender !== ParentGender.MALE && profileState.gender !== ParentGender.FEMALE;
        primaryButton = (
          <button
            onClick={handleGenderNext}
            disabled={isGenderDisabled}
            className={isGenderDisabled ? btnStyleDisabled : btnStylePrimary}
          >
            <p className="text-body text-white text-[24px]">
              {I18n.t("MSG_PARENT_ONBOARDING_BTN_NEXT")}
            </p>
          </button>
        );
      } else if (profileSubStep === ProfileSubStep.BIRTHDAY) {
        const isBirthdayDisabled = !isBirthdayValid;
        primaryButton = (
          <button
            onClick={handleBirthdayNext}
            disabled={isBirthdayDisabled}
            className={isBirthdayDisabled ? btnStyleDisabled : btnStylePrimary}
          >
            <p className="text-body text-white text-[24px]">
              {I18n.t("MSG_PARENT_ONBOARDING_BTN_NEXT")}
            </p>
          </button>
        );
      } else if (profileSubStep === ProfileSubStep.APPEARANCE) {
        const isAppearanceDisabled =
          !profileState.glasses ||
          !profileState.hairstyle ||
          !profileState.bodyType ||
          !profileState.faceShape;
        primaryButton = (
          <button
            onClick={handleAppearanceNext}
            disabled={isAppearanceDisabled}
            className={isAppearanceDisabled ? btnStyleDisabled : btnStylePrimary}
          >
            <p className="text-body text-white text-[24px]">
              {I18n.t("MSG_PARENT_ONBOARDING_BTN_NEXT")}
            </p>
          </button>
        );
      } else if (profileSubStep === ProfileSubStep.WORK) {
        const isWorkDisabled =
          !profileState.occupation ||
          !profileState.jobTitle ||
          !profileState.industry ||
          !profileState.annual_income;
        primaryButton = (
          <button
            onClick={handleWorkNext}
            disabled={isWorkDisabled}
            className={isWorkDisabled ? btnStyleDisabled : btnStylePrimary}
          >
            <p className="text-body text-white text-[24px]">
              {I18n.t("MSG_PARENT_ONBOARDING_BTN_NEXT")}
            </p>
          </button>
        );
      } else if (profileSubStep === ProfileSubStep.REGION) {
        const isRegionDisabled = !profileState.region || !profileState.city;
        primaryButton = (
          <button
            onClick={handleRegionNext}
            disabled={isRegionDisabled}
            className={isRegionDisabled ? btnStyleDisabled : btnStylePrimary}
          >
            <p className="text-body text-white text-[24px]">
              {I18n.t("MSG_PARENT_ONBOARDING_BTN_NEXT")}
            </p>
          </button>
        );
      } else if (profileSubStep === ProfileSubStep.FAMILY) {
        const isFamilyDisabled =
          !profileState.userRole || profileState.householdMembers.length === 0;
        primaryButton = (
          <button
            onClick={handleFamilyNext}
            disabled={isFamilyDisabled}
            className={isFamilyDisabled ? btnStyleDisabled : btnStylePrimary}
          >
            <p className="text-body text-white text-[24px]">
              {I18n.t("MSG_PARENT_ONBOARDING_BTN_GENERATE")}
            </p>
          </button>
        );
      } else if (profileSubStep === ProfileSubStep.ICON_PREVIEW) {
        if (isGenerating) {
          // 生成中はキャンセルボタンのみ
          return (
            <div className="h-[80px] flex-row-el flex-center gap-4 px-6 pb-6 alpha">
              <button onClick={handleCancelGeneration} className={btnStyleDanger}>
                {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
              </button>
            </div>
          );
        }
        primaryButton = (
          <button
            onClick={handleIconConfirm}
            disabled={!generatedImage || isGenerating}
            className={generatedImage && !isGenerating ? btnStylePrimary : btnStyleDisabled}
          >
            <p className="text-body text-white text-[24px]">
              {I18n.t("MSG_PARENT_ONBOARDING_BTN_CONFIRM")}
            </p>
          </button>
        );
      }
      // GENDER: 自動遷移なのでprimaryButtonなし
    } else if (state.iconMethod === IconMethod.UPLOAD) {
      primaryButton = (
        <button
          onClick={handleUploadConfirm}
          disabled={!state.icon}
          className={state.icon ? btnStylePrimary : btnStyleDisabled}
        >
          <p className="text-body text-white text-[24px]">{I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}</p>
        </button>
      );
    } else if (state.iconMethod === IconMethod.PRESET) {
      primaryButton = (
        <button
          onClick={handlePresetConfirm}
          disabled={!state.icon}
          className={state.icon ? btnStylePrimary : btnStyleDisabled}
        >
          <p className="text-body text-white text-[24px]">{I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}</p>
        </button>
      );
    }

    return (
      <div className="h-[80px] flex-row-el flex-center gap-4 px-6 pb-6 alpha">
        {state.iconMethod !== null && (
          <button onClick={handleBack} className={btnStyleCancel}>
            <p className="text-body text-textcolor-black text-[24px]">
              {state.iconMethod === IconMethod.UPLOAD || state.iconMethod === IconMethod.PRESET
                ? I18n.t("MSG_BACK_BTN_BY_PARENT")
                : I18n.t("MSG_PARENT_ONBOARDING_BTN_BACK")}
            </p>
          </button>
        )}
        {primaryButton}
      </div>
    );
  };

  // ヘッダー（現在のアイコン → 選択中のアイコン）
  const renderHeader = () => {
    if (state.iconMethod === null) {
      return null;
    }

    // AI生成フロー中は、ICON_PREVIEWステップでのみ生成画像を表示
    // 生成中はヘッダーにはプレースホルダーを表示（進捗はメインプレビューで表示）
    const displayIcon =
      state.iconMethod === IconMethod.AI
        ? profileSubStep === ProfileSubStep.ICON_PREVIEW
          ? isGenerating
            ? null
            : generatedImage || partialImage
          : null
        : state.icon || generatedImage;

    return (
      <div className="flex-col-el">
        {/* 標準タイトルヘッダー */}
        <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0 px-6 pt-6">
          <span className="material-symbols-outlined !text-[54px] text-red-100">edit</span>
          <h2 className="text-[33px] text-textcolor-black whitespace-nowrap">
            {I18n.t("MSG_PARENT_PAGE_ICON")}
          </h2>
        </div>
        {/* アイコンプレビュー（既存）*/}
        <div className="flex-row-el items-center justify-center pt-4 px-4">
          <div className="flex-row-el flex-center">
            {/* 現在のアイコン */}
            <div className="flex-col-el flex-center gap-1">
              {_currentIcon ? (
                <UserAvatar size={100} icon={_currentIcon} />
              ) : (
                <div
                  className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                  style={{
                    boxShadow:
                      "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                  }}
                >
                  <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden bg-gray-10">
                    <span className="material-symbols-outlined text-[32px] text-gray-30">
                      person
                    </span>
                  </div>
                </div>
              )}
              <p className="text-[16px] text-textcolor-black m-0">
                {I18n.t("MSG_ICON_CHANGE_PREVIEW_CURRENT_PARENT")}
              </p>
            </div>

            {/* 矢印 */}
            <div className="flex-col-el flex-center gap-1 shrink-0">
              <div className="w-[60px] h-[100px] flex-col-el flex-center">
                <span className="material-symbols-outlined text-[28px] text-gray-40">
                  arrow_forward
                </span>
              </div>
              <p className="text-[16px] text-textcolor-black opacity-0 m-0">placeholder</p>
            </div>

            {/* 選択中のアイコン */}
            <div className="flex-col-el flex-center gap-1">
              {isGenerating && state.iconMethod === IconMethod.AI ? (
                <div className="flex-col-el flex-center w-[100px] h-[100px] bg-beige-20 rounded-full border-2 border-gray-20 overflow-hidden">
                  <CircularProgress progress={progress} size={84} />
                </div>
              ) : displayIcon ? (
                <UserAvatar size={100} icon={displayIcon} />
              ) : (
                <div
                  className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                  style={{
                    boxShadow:
                      "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                  }}
                >
                  <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden bg-gray-10">
                    <span className="material-symbols-outlined text-[32px] text-gray-30">
                      add_photo_alternate
                    </span>
                  </div>
                </div>
              )}
              <p className="text-[16px] text-textcolor-black m-0">
                {I18n.t("MSG_ICON_CHANGE_PREVIEW_SELECTED_PARENT")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div
          className="flex-col-el flex-center relative bg-gray2-30 rounded-[16px]"
          style={{
            width: MODAL_WIDTH * scale,
            height: MODAL_HEIGHT * scale,
          }}
        >
          <div
            className="flex-col-el shrink-0 border-solid border-gray2-30 border-[16px] rounded-[16px] relative"
            style={{
              width: MODAL_WIDTH,
              height: MODAL_HEIGHT,
              transform: `scale(${scale})`,
            }}
          >
            <div className="flex-col-el w-full h-full rounded-[8px] bg-white relative">
              <ParentCloseBtn close={handleClose} />

              {state.iconMethod === null ? (
                <div className="flex-col-el w-full h-full">
                  {/* タイトルヘッダー */}
                  <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0 px-6 pt-6">
                    <span className="material-symbols-outlined !text-[54px] text-red-100">
                      edit
                    </span>
                    <h2 className="text-[33px] text-textcolor-black whitespace-nowrap">
                      {I18n.t("MSG_PARENT_PAGE_ICON")}
                    </h2>
                  </div>
                  <div className="flex-col-el w-full h-full p-4 overflow-hidden">
                    {renderContent()}
                  </div>
                </div>
              ) : (
                <>
                  {renderHeader()}
                  <div className="flex-col-el w-full h-full p-4 overflow-hidden">
                    {renderContent()}
                  </div>
                  {renderFooter()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
